import { useEffect, useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { useMutation } from '@apollo/client'
import { UPDATE_CHARTER_BOAT_BOOKING } from 'graphql/charterBoats/mutations'

export function UpdateBoatDialog({ status, title, onClose, onSuccess, booking }) {
    const { t } = useTranslation()
    const open = status

    const [segmentUpdates, setSegmentUpdates] = useState([])
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Updating Booking'))

    // Initialize segment updates for all segments
    useEffect(() => {
        if (booking?.trip_segments) {
            const formatted = booking.trip_segments.map(segment => {
                const firstLocation = segment.trip_locations?.[0] || {}
                return {
                    segment_id: segment.segment_id,
                    travel_date: segment.travel_date || '',
                    departure_time: firstLocation.departure_time || '',
                    arrival_time: firstLocation.arrival_time || '',
                }
            })
            setSegmentUpdates(formatted)
        }
    }, [booking])

    // Reset dialog state when opened/closed
    useEffect(() => {
        if (status) {
            setShowUpdateConfirm(false)
        } else {
            setShowUpdateConfirm(false)
        }
    }, [status])

    const handleSegmentChange = (index, field, value) => {
        const updated = [...segmentUpdates]
        
        // Ensure date format is YYYY-MM-DD
        if (field === 'travel_date' && value) {
            // If user typed it in different format, try to convert
            if (value.length > 0 && !value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // Try to parse and reformat if needed
                const date = new Date(value)
                if (!isNaN(date)) {
                    value = date.toISOString().split('T')[0]
                }
            }
        }
        
        updated[index][field] = value
        setSegmentUpdates(updated)
    }

    const isValid = segmentUpdates.every(seg => seg.travel_date && seg.departure_time && seg.arrival_time)

    const [updateCharterBoatBooking] = useMutation(UPDATE_CHARTER_BOAT_BOOKING, {
        onCompleted: data => {
            if (data.updateCharterBoatBooking.code === '200' && data.updateCharterBoatBooking.success) {
                setSubmitLoading(false)
                const updatedBooking = data.updateCharterBoatBooking.booking
                onSuccess(updatedBooking)
            } else {
                setSubmitLoading(false)
                setAddError(data.updateCharterBoatBooking.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleUpdateConfirm = () => {
        setSubmitLoading(true)

        const formattedSegments = segmentUpdates.map(seg => ({
            segment_id: seg.segment_id,
            travel_date: seg.travel_date,
            trip_departure_time: seg.departure_time.length === 5 ? `${seg.departure_time}:00` : seg.departure_time,
            trip_arrival_time: seg.arrival_time.length === 5 ? `${seg.arrival_time}:00` : seg.arrival_time,
        }))

        console.log('Sending to mutation:', formattedSegments)

        updateCharterBoatBooking({
            variables: {
                booking: {
                    booking_id: booking.booking_id,
                    trip_segments: formattedSegments,
                },
            },
        })
    }

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75" />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            {!showUpdateConfirm && (
                                <>
                                    <h3 className="text-lg font-bold text-primary ps-2">{title}</h3>
                                    <div className="mt-4 space-y-4 max-h-[400px] overflow-y-auto">
                                        {segmentUpdates.map((segment, index) => (
                                            <div key={segment.segment_id} className="bg-slate-50 p-4 rounded-md">
                                                <h4 className="font-semibold mb-3">
                                                    {t('Segment')} {index + 1}
                                                </h4>

                                                <div className="mb-3">
                                                    <label className="text-sm font-medium">{t('Travel Date')} *</label>
                                                    <input
                                                        type="date"
                                                        value={segment.travel_date}
                                                        onChange={e =>
                                                            handleSegmentChange(index, 'travel_date', e.target.value)
                                                        }
                                                        className="block w-full rounded-md border py-1.5 px-2"
                                                    />
                                                </div>

                                                {/* Departure Time first */}
                                                <div className="mb-3">
                                                    <label className="text-sm font-medium">
                                                        {t('Departure Time')} *
                                                    </label>
                                                    <input
                                                        type="time"
                                                        value={segment.departure_time}
                                                        onChange={e =>
                                                            handleSegmentChange(index, 'departure_time', e.target.value)
                                                        }
                                                        className="block w-full rounded-md border py-1.5 px-2"
                                                    />
                                                </div>

                                                {/* Then Arrival Time */}
                                                <div className="mb-3">
                                                    <label className="text-sm font-medium">{t('Arrival Time')} *</label>
                                                    <input
                                                        type="time"
                                                        value={segment.arrival_time}
                                                        onChange={e =>
                                                            handleSegmentChange(index, 'arrival_time', e.target.value)
                                                        }
                                                        className="block w-full rounded-md border py-1.5 px-2"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {!showUpdateConfirm && (
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>

                                    <button
                                        disabled={!isValid}
                                        type="button"
                                        onClick={() => setShowUpdateConfirm(true)}
                                        className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                                            isValid
                                                ? 'bg-primary hover:bg-primary/[0.9]'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {showUpdateConfirm && (
                            <>
                                <div className="bg-white px-4 pb-4 pt-0 sm:pl-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                                        </div>
                                        <div className="mt-3 sm:ml-4 sm:mt-0 sm:text-left pb-3">
                                            <DialogTitle className="text-md font-bold">
                                                {t('Are you sure?')}
                                            </DialogTitle>

                                            <div className="mt-2 space-y-1">
                                                {segmentUpdates.map((seg, i) => (
                                                    <p key={i} className="text-sm text-gray-700">
                                                        {t('Segment')} {i + 1}: {seg.travel_date} |{' '}
                                                        {seg.arrival_time.length === 5
                                                            ? `${seg.arrival_time}:00`
                                                            : seg.arrival_time}{' '}
                                                        -{' '}
                                                        {seg.departure_time.length === 5
                                                            ? `${seg.departure_time}:00`
                                                            : seg.departure_time}
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowUpdateConfirm(false)}
                                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            {t('No')}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleUpdateConfirm}
                                            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                        >
                                            {t('Yes')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {showErrDialog && (
                            <ErrorDialog
                                status={true}
                                title="Error"
                                message={addError}
                                onClose={() => setShowErrDialog(false)}
                            />
                        )}

                        {submitLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}
